'use client';

import type { CSSProperties } from 'react';
import { HONEYPOT_FIELD } from '@/lib/form-guard';

/**
 * Visually and programmatically removes content from the page while leaving it
 * in the DOM for bots to find.
 *
 * Deliberately not `display:none` or `type="hidden"` — both are the first
 * things a scraper skips. Off-screen clipping keeps the field "real" to a
 * naive parser while staying unreachable for sighted users, screen readers,
 * and keyboard navigation.
 */
export const honeypotWrapperStyle: CSSProperties = {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
    opacity: 0,
    pointerEvents: 'none',
};

type HoneypotFieldProps = {
    value: string;
    onChange: (value: string) => void;
    /** Disambiguates the DOM id when more than one form shares a page. */
    idSuffix?: string;
};

/**
 * Decoy input for public forms. Any value at submit time means the submission
 * was scripted — real users cannot reach this field.
 */
export function HoneypotField({ value, onChange, idSuffix = '' }: HoneypotFieldProps) {
    const id = `${HONEYPOT_FIELD}${idSuffix}`;

    return (
        <div aria-hidden="true" style={honeypotWrapperStyle}>
            <label htmlFor={id}>Leave this field blank</label>
            <input
                id={id}
                name={HONEYPOT_FIELD}
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={value}
                onChange={e => onChange(e.target.value)}
            />
        </div>
    );
}
